import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';
import InputAdornment from '@mui/material/InputAdornment';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Restore as RestoreIcon,
  Visibility as VisibilityIcon,
  CallMerge as CallMergeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import BusinessDetails from './BusinessDetails';
import BusinessMergeDialog from './BusinessMergeDialog';

interface Address {
  id?: string;
  street?: string;
  city?: string;
  state?: string;
  postCode?: string;
  country?: string;
}

interface Business {
  id: string;
  name: string;
  abn: string;
  address?: Address;
  dateCreated: string;
  archivedDate?: string;
  deviceCount: number;
  contactCount: number;
}

interface BusinessFormData {
  name: string;
  abn: string;
  primaryUserId: string;
  address: {
    street: string;
    city: string;
    state: string;
    postCode: string;
    country: string;
  };
}

interface UserOption {
  id: string;
  email: string;
  fullName?: string;
  isActive: boolean;
}

const BusinessManagement: React.FC = () => {
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [mergeTarget, setMergeTarget] = useState<Business | null>(null);
  const [searchText, setSearchText] = useState('');
  type SortField = 'name' | 'abn' | 'location' | 'deviceCount' | 'contactCount' | 'dateCreated';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const displayedBusinesses = React.useMemo(() => {
    const q = searchText.toLowerCase();
    const filtered = q
      ? businesses.filter(
          (b) =>
            b.name.toLowerCase().includes(q) ||
            (b.abn || '').includes(q) ||
            (b.address?.city || '').toLowerCase().includes(q) ||
            (b.address?.state || '').toLowerCase().includes(q),
        )
      : businesses;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':        cmp = a.name.localeCompare(b.name); break;
        case 'abn':         cmp = (a.abn || '').localeCompare(b.abn || ''); break;
        case 'location':    cmp = (a.address?.city || '').localeCompare(b.address?.city || ''); break;
        case 'deviceCount': cmp = a.deviceCount - b.deviceCount; break;
        case 'contactCount':cmp = a.contactCount - b.contactCount; break;
        case 'dateCreated': cmp = new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime(); break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [businesses, searchText, sortField, sortDir]);
  const [formData, setFormData] = useState<BusinessFormData>({
    name: '',
    abn: '',
    primaryUserId: '',
    address: {
      street: '',
      city: '',
      state: '',
      postCode: '',
      country: 'Australia',
    },
  });

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: { Authorization: `Bearer ${auth.tokens?.accessToken}` },
      });
      if (response.ok) {
        const data: UserOption[] = await response.json();
        setUsers(data.filter((u) => u.isActive));
      }
    } catch {
      // best-effort — the Primary User select just stays empty if this fails
    }
  };

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      
      // Check authentication before making request
      if (!auth.isSignedIn || !auth.tokens?.accessToken) {
        setError('Not authenticated. Please log in.');
        setLoading(false);
        return;
      }
      
      // Debug logging
      console.log('Auth state:', {
        isAuthenticated: auth.isSignedIn,
        hasUser: !!auth.user,
        hasAccessToken: !!auth.tokens?.accessToken,
        tokenPreview: auth.tokens?.accessToken?.substring(0, 20) + '...'
      });
      
      const response = await fetch(
        `/api/businesses?includeArchived=${includeArchived}`,
        {
          headers: {
            Authorization: `Bearer ${auth.tokens?.accessToken}`,
          },
        }
      );

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch businesses: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setBusinesses(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if authenticated and have access token
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchBusinesses();
      fetchUsers();
    }
  }, [includeArchived, auth.isSignedIn, auth.tokens?.accessToken]);

  const handleOpenDialog = (business?: Business) => {
    if (business) {
      setEditingBusiness(business);
      setFormData({
        name: business.name,
        abn: business.abn,
        primaryUserId: '',
        address: {
          street: business.address?.street || '',
          city: business.address?.city || '',
          state: business.address?.state || '',
          postCode: business.address?.postCode || '',
          country: business.address?.country || 'Australia',
        },
      });
    } else {
      setEditingBusiness(null);
      setFormData({
        name: '',
        abn: '',
        primaryUserId: '',
        address: {
          street: '',
          city: '',
          state: '',
          postCode: '',
          country: 'Australia',
        },
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBusiness(null);
    setFormData({
      name: '',
      abn: '',
      primaryUserId: '',
      address: {
        street: '',
        city: '',
        state: '',
        postCode: '',
        country: 'Australia',
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const url = editingBusiness
        ? `/api/businesses/${editingBusiness.id}`
        : `/api/businesses`;

      const method = editingBusiness ? 'PUT' : 'POST';

      const payload = editingBusiness
        ? {
            name: formData.name,
            abn: formData.abn,
            address: formData.address.street ? formData.address : null,
          }
        : {
            name: formData.name,
            abn: formData.abn,
            primaryUserId: formData.primaryUserId,
            address: formData.address.street ? formData.address : null,
          };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save business');
      }

      handleCloseDialog();
      fetchBusinesses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Permanently delete this business? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/businesses/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete business');
      }

      fetchBusinesses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const response = await fetch(
        `/api/businesses/${id}/restore`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${auth.tokens?.accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to restore business');
      }

      fetchBusinesses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Show BusinessDetails if a business is selected
  if (selectedBusinessId) {
    return (
      <BusinessDetails
        businessId={selectedBusinessId}
        onBack={() => setSelectedBusinessId(null)}
      />
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={1.5}
        mb={3}
      >
        <Typography variant="h5" component="h2">
          Business Management
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
              />
            }
            label="Show Archived"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Business
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        placeholder="Search by name, ABN or location…"
        size="small"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {displayedBusinesses.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {searchText ? 'No businesses match your search.' : 'No businesses found. Click "Add Business" to create one.'}
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {displayedBusinesses.map((business) => (
            <Card key={business.id} variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon color="action" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={600}>{business.name}</Typography>
                  </Box>
                  {business.archivedDate ? (
                    <Chip label="Archived" size="small" color="default" />
                  ) : (
                    <Chip label="Active" size="small" color="success" />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block' }}>
                  ABN {business.abn}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {business.address
                    ? (business.address.city && business.address.state
                      ? `${business.address.city}, ${business.address.state}`
                      : business.address.city || business.address.state || '-')
                    : '-'}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <Chip label={`${business.deviceCount} devices`} size="small" color="primary" />
                  <Chip label={`${business.contactCount} contacts`} size="small" color="secondary" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {new Date(business.dateCreated).toLocaleDateString()}
                  </Typography>
                </Box>
              </CardContent>
              <CardActions>
                {business.archivedDate ? (
                  <>
                    <Button size="small" startIcon={<RestoreIcon fontSize="small" />} onClick={() => handleRestore(business.id)}>
                      Restore
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon fontSize="small" />}
                      onClick={() => handleDelete(business.id)}
                      disabled={business.deviceCount > 0}
                    >
                      Delete
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="small" startIcon={<VisibilityIcon fontSize="small" />} onClick={() => setSelectedBusinessId(business.id)}>
                      View
                    </Button>
                    <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => handleOpenDialog(business)}>
                      Edit
                    </Button>
                    <Button size="small" startIcon={<CallMergeIcon fontSize="small" />} onClick={() => setMergeTarget(business)}>
                      Merge
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteIcon fontSize="small" />}
                      onClick={() => handleDelete(business.id)}
                      disabled={business.deviceCount > 0}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </CardActions>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ '& .MuiTableCell-root': { py: 1 } }}>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortField === 'name' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDir : 'asc'} onClick={() => handleSort('name')}>Name</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'abn' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'abn'} direction={sortField === 'abn' ? sortDir : 'asc'} onClick={() => handleSort('abn')}>ABN</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'location' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'location'} direction={sortField === 'location' ? sortDir : 'asc'} onClick={() => handleSort('location')}>Location</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'deviceCount' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'deviceCount'} direction={sortField === 'deviceCount' ? sortDir : 'asc'} onClick={() => handleSort('deviceCount')}>Devices</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'contactCount' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'contactCount'} direction={sortField === 'contactCount' ? sortDir : 'asc'} onClick={() => handleSort('contactCount')}>Contacts</TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell sortDirection={sortField === 'dateCreated' ? sortDir : false}>
                  <TableSortLabel active={sortField === 'dateCreated'} direction={sortField === 'dateCreated' ? sortDir : 'asc'} onClick={() => handleSort('dateCreated')}>Created</TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedBusinesses.map((business) => (
                <TableRow key={business.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon color="action" />
                      {business.name}
                    </Box>
                  </TableCell>
                  <TableCell>{business.abn}</TableCell>
                  <TableCell>
                    {business.address ? (
                      <Box>
                        {business.address.city && business.address.state
                          ? `${business.address.city}, ${business.address.state}`
                          : business.address.city || business.address.state || '-'}
                      </Box>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={business.deviceCount} size="small" color="primary" />
                  </TableCell>
                  <TableCell>
                    <Chip label={business.contactCount} size="small" color="secondary" />
                  </TableCell>
                  <TableCell>
                    {business.archivedDate ? (
                      <Chip label="Archived" size="small" color="default" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(business.dateCreated).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    {business.archivedDate ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => handleRestore(business.id)}
                          title="Restore business"
                        >
                          <RestoreIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(business.id)}
                          title="Delete business"
                          disabled={business.deviceCount > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => setSelectedBusinessId(business.id)}
                          title="View details"
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(business)}
                          title="Edit business"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setMergeTarget(business)}
                          title="Merge into/from another business"
                        >
                          <CallMergeIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(business.id)}
                          title="Delete business"
                          disabled={business.deviceCount > 0}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBusiness ? 'Edit Business' : 'Add New Business'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ABN (11 digits, optional)"
                  value={formData.abn}
                  onChange={(e) =>
                    setFormData({ ...formData, abn: e.target.value.replace(/\D/g, '') })
                  }
                  inputProps={{ maxLength: 11 }}
                  helperText="Australian Business Number (11 digits) — optional"
                />
              </Grid>
              {!editingBusiness && (
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Primary User</InputLabel>
                    <Select
                      value={formData.primaryUserId}
                      label="Primary User"
                      onChange={(e) => setFormData({ ...formData, primaryUserId: e.target.value })}
                    >
                      {users.length === 0 ? (
                        <MenuItem disabled><em>No users available</em></MenuItem>
                      ) : (
                        users.map((u) => (
                          <MenuItem key={u.id} value={u.id}>
                            {u.fullName ? `${u.fullName} — ${u.email}` : u.email}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Address (Optional)
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.address.street}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, street: e.target.value },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.address.state}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, state: e.target.value },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Post Code"
                  value={formData.address.postCode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, postCode: e.target.value },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.address.country}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, country: e.target.value },
                    })
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || (formData.abn.length > 0 && formData.abn.length !== 11) || (!editingBusiness && !formData.primaryUserId)}
          >
            {editingBusiness ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <BusinessMergeDialog
        open={mergeTarget !== null}
        onClose={() => setMergeTarget(null)}
        onMerged={fetchBusinesses}
        initialBusiness={mergeTarget ? { id: mergeTarget.id, name: mergeTarget.name, abn: mergeTarget.abn } : null}
      />
    </Box>
  );
};

export default BusinessManagement;
